import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import { PermissionStatus } from 'expo-location';
import { Platform } from 'react-native';

export type LocationAccessResult =
  | { ok: true }
  | { ok: false; reason: 'denied' | 'unavailable' | 'unknown' };

/** Estado desglosado para UI (permiso del sistema vs. GPS/servicio de ubicación). */
export type ForegroundLocationStatusDetail = {
  permisoOtorgado: boolean;
  ubicacionActiva: boolean;
};

const SESSION_VERIFIED_KEY = 'comensal_location_verified_v1';

/** Recuerda en la sesión que el usuario aceptó el permiso (p. ej. GPS apagado pero permiso sí). */
const GEO_PERMISSION_GRANTED_SESSION_KEY = 'geo_permission_granted_v1';

/** Temporal (pruebas): quitar antes de producción. */
const BYPASS_SESSION_KEY = 'comensal_location_bypass_dev_v1';

type WebGeolocationPermissionState = 'granted' | 'denied' | 'prompt';

async function queryWebGeolocationPermissionState(): Promise<WebGeolocationPermissionState | null> {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !('permissions' in navigator)) {
    return null;
  }
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    if (result.state === 'granted') return 'granted';
    if (result.state === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return null;
  }
}

/** Llamar cuando requestForegroundPermissionsAsync devuelve granted (aunque falle el GPS después). */
export async function noteGeolocationPermissionGranted(): Promise<void> {
  await storageSet(GEO_PERMISSION_GRANTED_SESSION_KEY, '1');
}

export async function clearGeolocationPermissionGrantedNote(): Promise<void> {
  await storageSet(GEO_PERMISSION_GRANTED_SESSION_KEY, null);
}

async function isPermisoUbicacionOtorgado(): Promise<boolean> {
  const { status } = await Location.getForegroundPermissionsAsync();
  if (status === PermissionStatus.GRANTED) {
    await noteGeolocationPermissionGranted();
    return true;
  }
  if (status === PermissionStatus.DENIED) {
    await clearGeolocationPermissionGrantedNote();
    return false;
  }

  if (Platform.OS === 'web') {
    const webState = await queryWebGeolocationPermissionState();
    if (webState === 'granted') {
      await noteGeolocationPermissionGranted();
      return true;
    }
    if (webState === 'denied') {
      await clearGeolocationPermissionGrantedNote();
      return false;
    }
  }

  const remembered = await storageGet(GEO_PERMISSION_GRANTED_SESSION_KEY);
  return remembered === '1';
}

async function storageGet(key: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    if (typeof sessionStorage !== 'undefined') {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    }
    return null;
  }
  return AsyncStorage.getItem(key);
}

async function storageSet(key: string, value: string | null): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof sessionStorage !== 'undefined') {
      try {
        if (value == null) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, value);
      } catch {
        /* private mode, etc. */
      }
    }
    return;
  }
  if (value == null) await AsyncStorage.removeItem(key);
  else await AsyncStorage.setItem(key, value);
}

export async function isLocationBypassForSessionAsync(): Promise<boolean> {
  const v = await storageGet(BYPASS_SESSION_KEY);
  return v === '1';
}

/** @deprecated usar isLocationBypassForSessionAsync */
export function isLocationBypassForSession(): boolean {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      return sessionStorage.getItem(BYPASS_SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }
  return false;
}

export async function setLocationBypassForSession(enabled: boolean): Promise<void> {
  await storageSet(BYPASS_SESSION_KEY, enabled ? '1' : null);
}

/**
 * Temporal (pruebas): omitir comprobaciones de GPS/monitor. Eliminar junto al UI de bypass.
 */
export async function enableLocationBypassForDevTesting(): Promise<void> {
  await setLocationBypassForSession(true);
  await setLocationVerifiedThisSession(true);
}

export async function isLocationVerifiedThisSessionAsync(): Promise<boolean> {
  const v = await storageGet(SESSION_VERIFIED_KEY);
  return v === '1';
}

/** @deprecated usar isLocationVerifiedThisSessionAsync (en RN sessionStorage no existe) */
export function isLocationVerifiedThisSession(): boolean {
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      return sessionStorage.getItem(SESSION_VERIFIED_KEY) === '1';
    } catch {
      return false;
    }
  }
  return false;
}

export async function setLocationVerifiedThisSession(value: boolean): Promise<void> {
  await storageSet(SESSION_VERIFIED_KEY, value ? '1' : null);
}

/** Permiso de primer plano y servicio de ubicación del sistema, por separado. */
export async function getForegroundLocationStatusDetail(): Promise<ForegroundLocationStatusDetail> {
  if (await isLocationBypassForSessionAsync()) {
    return { permisoOtorgado: true, ubicacionActiva: true };
  }
  try {
    const permisoOtorgado = await isPermisoUbicacionOtorgado();
    let ubicacionActiva = false;
    try {
      ubicacionActiva = await Location.hasServicesEnabledAsync();
    } catch {
      ubicacionActiva = false;
    }
    return { permisoOtorgado, ubicacionActiva };
  } catch {
    return { permisoOtorgado: false, ubicacionActiva: false };
  }
}

/**
 * Comprueba permiso de primer plano y que el servicio de ubicación del sistema esté activo.
 * No exige getCurrentPosition: en emulador/interior suele fallar aunque el permiso sea correcto.
 */
export async function getForegroundLocationAccessSnapshot(): Promise<LocationAccessResult> {
  if (await isLocationBypassForSessionAsync()) return { ok: true };
  try {
    const { status } = await Location.getForegroundPermissionsAsync();
    if (status !== PermissionStatus.GRANTED) {
      return { ok: false, reason: status === PermissionStatus.DENIED ? 'denied' : 'unavailable' };
    }
    const servicesOn = await Location.hasServicesEnabledAsync();
    if (!servicesOn) {
      return { ok: false, reason: 'unavailable' };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: 'unknown' };
  }
}

/**
 * Pide permiso si hace falta y comprueba servicios. Intenta una lectura de posición de forma opcional.
 */
export async function requestLocationAndVerify(): Promise<LocationAccessResult> {
  if (await isLocationBypassForSessionAsync()) return { ok: true };
  try {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== PermissionStatus.GRANTED) {
      return { ok: false, reason: 'denied' };
    }
    const servicesOn = await Location.hasServicesEnabledAsync();
    if (!servicesOn) {
      return { ok: false, reason: 'unavailable' };
    }
    try {
      await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Low,
        maximumAge: 120_000,
      });
    } catch {
      /* Permiso y GPS del sistema OK; sin fix todavía no bloquea el flujo */
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: 'unknown' };
  }
}
